/**
 * Quota Constants and Utility Functions
 *
 * This module provides centralized constants and helper functions for handling
 * "unlimited" quota values consistently across the frontend codebase.
 *
 * UNLIMITED VALUE CONVENTION:
 * ---------------------------
 * - API Response: -1 = unlimited (with unlimited_credits=true flag)
 * - Legacy: 999999 or higher was used as "unlimited" - still supported for backward compat
 *
 * BEST PRACTICE:
 * --------------
 * Always use the `isUnlimited()` helper function instead of checking magic numbers.
 * The function handles all cases: null, undefined, -1, and legacy large values.
 */

/** Sentinel value used in API to indicate "unlimited" */
export const UNLIMITED_SENTINEL = -1

/** Legacy threshold for "unlimited" - values >= this are treated as unlimited */
export const LEGACY_UNLIMITED_THRESHOLD = 999999

/** Default quota values when no license is found */
export const DEFAULT_FLEX_CREDITS = 0
export const DEFAULT_FLEX_USERS = 1

/**
 * Check if a quota value represents "unlimited".
 *
 * This function handles all conventions used in the codebase:
 * - null/undefined: No limit set
 * - -1: API representation of unlimited
 * - >= 999999: Legacy representation of unlimited
 *
 * @param value - The quota value to check (credits_limit, user_limit, etc.)
 * @returns True if the value represents unlimited, False otherwise
 *
 * @example
 * isUnlimited(null)      // true
 * isUnlimited(undefined) // true
 * isUnlimited(-1)        // true
 * isUnlimited(999999)    // true
 * isUnlimited(100)       // false
 * isUnlimited(0)         // false
 */
export function isUnlimited(value: number | null | undefined): boolean {
  if (value === null || value === undefined) {
    return true
  }
  if (value === UNLIMITED_SENTINEL) {
    return true
  }
  if (value >= LEGACY_UNLIMITED_THRESHOLD) {
    return true
  }
  return false
}

/**
 * Check if quota data indicates unlimited credits.
 * Checks both the explicit flag and the value.
 *
 * @param quota - Quota object from API response
 * @returns True if credits are unlimited
 */
export function hasUnlimitedCredits(quota: {
  unlimited_credits?: boolean
  credits_limit?: number | null
}): boolean {
  if (quota.unlimited_credits) {
    return true
  }
  return isUnlimited(quota.credits_limit)
}

/**
 * Check if quota data indicates unlimited users.
 * Checks both the explicit flag and the value.
 *
 * @param quota - Quota object from API response
 * @returns True if users are unlimited
 */
export function hasUnlimitedUsers(quota: {
  unlimited_users?: boolean
  user_limit?: number | null
}): boolean {
  if (quota.unlimited_users) {
    return true
  }
  return isUnlimited(quota.user_limit)
}

/**
 * Get a display-friendly representation of a quota limit.
 *
 * LABELING CONVENTION:
 * - "N/A" = No data available or not configured (null/undefined/0 with no unlimited flag)
 * - "Unlimited" = Unlimited quota (null with unlimited flag, -1, or >= 999999)
 * - number = Actual positive limit value
 *
 * @param value - The quota value
 * @param isExplicitlyUnlimited - Whether this is known to be unlimited (e.g., from unlimited_credits flag)
 * @param unlimitedText - Text to display for unlimited values (default: "Unlimited")
 * @param noDataText - Text to display when no data available (default: "N/A")
 * @returns String representation suitable for display
 */
export function getDisplayLimit(
  value: number | null | undefined,
  isExplicitlyUnlimited = false,
  unlimitedText = 'Unlimited',
  noDataText = 'N/A'
): string {
  // If explicitly marked as unlimited, show unlimited text
  if (isExplicitlyUnlimited) {
    return unlimitedText
  }
  // Check if the value itself indicates unlimited
  if (isUnlimited(value)) {
    return unlimitedText
  }
  // No value, null, undefined, or 0 = not configured
  if (value === null || value === undefined || value === 0) {
    return noDataText
  }
  // Return the actual positive value
  return value.toLocaleString()
}
